import { SubscriptionPlans } from "@prisma/client";

/**
 * Per-plan usage limits — the app-layer mirror of `apps/musicpy/server/quotas.py`.
 *
 * The Python file is the one that actually enforces: the Modal endpoints are reachable
 * without going through Next.js, so anything checked here can be skipped by calling
 * Modal directly from the browser. What this file buys is a good error message and
 * fast feedback before we pay for a container.
 *
 * Keep the numbers and the Redis key shapes identical to quotas.py. The keys matter as
 * much as the numbers — both sides read and write the same counters, so a typo here
 * silently detaches the check from the thing being counted.
 *
 * Approved in "Generation Limits Proposal" (Option A), 2026-08-04.
 */

export const DAY_SECONDS = 86_400;
export const MONTH_SECONDS = 30 * DAY_SECONDS;

/**
 * These used to be a single shared `generation:{userId}` counter across AI Song,
 * MusicGen and voice conversion, each checked against a different threshold. Split per
 * feature so a user blocked on one is not spending another's allowance.
 */
export const QuotaKeys = {
  song: (userId: string) => `gen:song:${userId}`,
  songVoice: (userId: string) => `gen:song_voice:${userId}`,
  convert: (userId: string) => `gen:convert:${userId}`,
  music: (userId: string) => `gen:music:${userId}`,
  stem: (userId: string) => `gen:stem:${userId}`,
  retrain: (userId: string, modelId: string) =>
    `train:retrain:${userId}:${modelId}`,
};

/**
 * Training runs per model per month, counting the first one. Voice slots alone do not
 * bound training: retraining an existing model creates no new model, so the slot count
 * never sees it. See MODEL_TRAINING_MAX_QUOTA in Features.ts for the slot side.
 */
export const RETRAINS_PER_MODEL: Record<SubscriptionPlans, number> = {
  [SubscriptionPlans.free]: 1,
  [SubscriptionPlans.starter]: 3,
  [SubscriptionPlans.pro]: 3,
  [SubscriptionPlans.studio]: 2,
  [SubscriptionPlans.label]: 2,
};

/** Training jobs a user may have running at once. */
export const CONCURRENT_TRAINING: Record<SubscriptionPlans, number> = {
  [SubscriptionPlans.free]: 1,
  [SubscriptionPlans.starter]: 1,
  [SubscriptionPlans.pro]: 1,
  [SubscriptionPlans.studio]: 3,
  [SubscriptionPlans.label]: 5,
};

/** AI Song allowance. Monthly, because a daily cap on 150/month means nothing. */
export const SONG_MONTHLY_QUOTA: Record<SubscriptionPlans, number | null> = {
  [SubscriptionPlans.free]: null, // capped daily instead, see FREE_SONG_DAILY
  [SubscriptionPlans.starter]: 150,
  [SubscriptionPlans.pro]: 400,
  [SubscriptionPlans.studio]: 1_000,
  [SubscriptionPlans.label]: 1_000,
};

/** How much of the monthly allowance may use voice targeting. Costs ~2x a plain song. */
export const SONG_VOICE_MONTHLY_QUOTA: Record<SubscriptionPlans, number> = {
  [SubscriptionPlans.free]: 0,
  [SubscriptionPlans.starter]: 0,
  [SubscriptionPlans.pro]: 100,
  [SubscriptionPlans.studio]: 300,
  [SubscriptionPlans.label]: 300,
};

export const FREE_SONG_DAILY = 5;

type MonthlySongPlan = Exclude<SubscriptionPlans, "free">;

export const getMonthlySongQuota = (plan: MonthlySongPlan): number => {
  const quota = SONG_MONTHLY_QUOTA[plan];

  if (quota === null) {
    throw new Error(`Monthly song quota is not configured for ${plan}`);
  }

  return quota;
};

export const getSongQuotaLabel = (plan: MonthlySongPlan): string => {
  const songQuota = getMonthlySongQuota(plan).toLocaleString("en-US");
  const voiceQuota = SONG_VOICE_MONTHLY_QUOTA[plan];

  if (voiceQuota === 0) {
    return `${songQuota} songs/mo, no voice targeting`;
  }

  return `${songQuota} songs/mo (${voiceQuota.toLocaleString(
    "en-US"
  )} with voice targeting)`;
};

/** Display numbers for the create-song sidebar counter. */
export const getSongQuotaDisplay = (
  plan?: SubscriptionPlans | null
): { limit: number; period: "daily" | "monthly" } => {
  if (!plan || plan === SubscriptionPlans.free) {
    return { limit: FREE_SONG_DAILY, period: "daily" };
  }

  const monthly = SONG_MONTHLY_QUOTA[plan];
  if (monthly == null) {
    return { limit: FREE_SONG_DAILY, period: "daily" };
  }

  return { limit: monthly, period: "monthly" };
};

/**
 * Display numbers for the voice-targeting sub-allowance. Always monthly — unlike
 * `getSongQuotaDisplay`, there's no free-daily variant, since free/starter get 0.
 */
export const getSongVoiceQuotaDisplay = (
  plan?: SubscriptionPlans | null
): { limit: number; period: "monthly" } => {
  return { limit: limitFor(SONG_VOICE_MONTHLY_QUOTA, plan), period: "monthly" };
};

/**
 * Fair-use ceilings on the features that stay "unlimited" — abuse protection, not quotas.
 *
 * Every paid ceiling is set ABOVE that plan's heaviest observed day, so no real user is
 * blocked. Measured over 14 days (65,965 conversions, 13,787 users), busiest single day
 * per user: starter peaked at 124, pro at 422, studio at 735, label at 210.
 *
 * The previous ceilings blocked 11 of 338 paying users, and the wrong 11 — our only label
 * customer and a studio user were the ones hitting walls. These clear every observed peak
 * and take paid denials to zero. Label sits at 1,000 for structure, not demand: the top
 * tier must not be capped below studio.
 *
 * Free stays at 3 deliberately — half of free users reach it and 3,074 were turned away in
 * the window, which is the upgrade pinch point doing its job.
 *
 * These hold only while real usage stays far below them; see quotas.py for the full
 * distribution, the cost caveat, and when to revisit.
 */
export const DAILY_FAIR_USE: Record<SubscriptionPlans, number> = {
  [SubscriptionPlans.free]: 3,
  [SubscriptionPlans.starter]: 200,
  [SubscriptionPlans.pro]: 500,
  [SubscriptionPlans.studio]: 1_000,
  [SubscriptionPlans.label]: 1_000,
};

/**
 * e.g. "200 conversions/day" — the single source for this copy across pricing surfaces
 * (plan cards, feature comparison table) so they can't drift into contradicting each
 * other. Routes through `limitFor` so an unrecognized plan fails closed to the free
 * ceiling instead of throwing.
 */
export const getDailyFairUseLabel = (
  plan: SubscriptionPlans | null | undefined
): string =>
  `${limitFor(DAILY_FAIR_USE, plan).toLocaleString("en-US")} conversions/day`;

/** Compact form for table cells, e.g. "200/day". Same source as {@link getDailyFairUseLabel}. */
export const getDailyFairUseCompactLabel = (
  plan: SubscriptionPlans | null | undefined
): string => `${limitFor(DAILY_FAIR_USE, plan).toLocaleString("en-US")}/day`;

/**
 * MusicGen keeps its own free number. It shared the old counter with AI Song, so
 * dropping free to 3 here would be a silent downgrade for existing free users.
 */
export const FREE_MUSIC_DAILY = 15;

/**
 * Look up a plan's limit, tolerating a plan that exists in Prisma but not in a table
 * here. Falling back to free fails closed, same as `limit_for` in quotas.py.
 */
export function limitFor<T>(
  table: Record<SubscriptionPlans, T>,
  plan: SubscriptionPlans | null | undefined
): T {
  if (plan && plan in table) {
    return table[plan];
  }
  return table[SubscriptionPlans.free];
}
